export type MasterKeyIntent = 'next' | 'prev' | 'open' | 'close';

interface KeyLike {
  key: string;
  defaultPrevented: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  target: EventTarget | null;
}

// Typing surfaces and dialogs own their keys.
const OWNS_KEYS =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="dialog"], [role="alertdialog"], [role="listbox"], [role="menu"]';
// Enter on a focused control activates that control, not the row cursor.
const ACTIVATES_ON_ENTER = 'button, a, [role="button"], [role="checkbox"]';

/** What a keydown means for the master table, or null to leave it alone. */
export function masterKeyIntent(e: KeyLike): MasterKeyIntent | null {
  if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return null;
  const target = e.target instanceof Element ? e.target : null;
  if (target?.closest(OWNS_KEYS)) return null;
  switch (e.key) {
    case 'j':
    case 'ArrowDown':
      return 'next';
    case 'k':
    case 'ArrowUp':
      return 'prev';
    case 'Enter':
      return target?.closest(ACTIVATES_ON_ENTER) ? null : 'open';
    case 'Escape':
      return 'close';
    default:
      return null;
  }
}

/**
 * Moves the cursor one row, clamped at both ends. With no cursor (or one that
 * is no longer in the list) it lands on the first row.
 */
export function stepCursor(
  keys: readonly string[],
  current: string | null,
  direction: 'next' | 'prev',
): string | null {
  if (keys.length === 0) return null;
  const index = current === null ? -1 : keys.indexOf(current);
  if (index === -1) return keys[0];
  const next = direction === 'next' ? index + 1 : index - 1;
  return keys[Math.min(keys.length - 1, Math.max(0, next))];
}

/**
 * The cursor if it is still one of the displayed rows, else null — so a page
 * change or filter never leaves Enter pointing at a row that is not visible.
 */
export function resolveCursor(
  keys: readonly string[],
  current: string | null,
): string | null {
  return current !== null && keys.includes(current) ? current : null;
}

/** What the master-table listener does with a key, given what is open. */
export type MasterKeyAction = 'clear-line' | 'clear-cart' | 'open' | 'step';

/**
 * Esc closes one level at a time (the selected line, then the open cart).
 * With a line selected, ↑/↓ belong to the line table (useLineStepKeys), so
 * the Orders cursor leaves them alone.
 */
export function masterKeyAction(
  intent: MasterKeyIntent,
  state: { lineSelected: boolean; cartOpen: boolean },
): MasterKeyAction | null {
  if (intent === 'open') return 'open';
  if (intent !== 'close') return state.lineSelected ? null : 'step';
  if (state.lineSelected) return 'clear-line';
  return state.cartOpen ? 'clear-cart' : null;
}
